/**
 * Pure placement maths for the guide overlay (DESIGN §3.2–3.4): where the speech bubble, its arrow and the Skip chip
 * go around a spotlight hole, where a tip docks, and where the glove stands. No DOM: guideOverlay.ts measures and
 * writes; this decides, so the rules are unit-tested (test/guideOverlayGeometry.test.ts). All values are CSS px in the
 * overlay root's coordinates.
 */

export interface Box { readonly x: number; readonly y: number; readonly w: number; readonly h: number }

export const GuideLayout = {
  /** The bubble's and tip's side gutters (mockups: 12 px each side of a 369 px bubble on a 393 px screen). */
  Inset: 12,
  /** Gap between the spotlight and a bubble hugging it. */
  Gap: 18,
  /** Gap between screen chrome (HUD, tray, tab bar) and a bubble docked against it. */
  BandGap: 12,
  /** The Skip chip (44 px) and its 6 px gap: the room a bubble keeps on its far side for it. */
  ChipSpace: 50,
  /** The Skip chip's size, for the overlap test (min-width 88 px, grows with its label). */
  ChipW: 100,
  ChipH: 44,
  /** The arrow notch stays this far inside the screen edges, clear of the bubble's rounded corners. */
  ArrowEdge: 28,
  /** A tip stays where it is while its moving target is at most this much further than Gap away (no jitter). */
  TipStick: 90,
  /** Spotlight padding around a DOM target, and around a world anchor's ring. */
  DomPad: 8,
  WorldPad: 6,
  /** A world hole is an ellipse this much flatter than wide: the iso ground is foreshortened. */
  WorldAspect: 0.85,
  /** The glove: a 64 px square whose fingertip is at (25, 4) in its own box (mockups/gen.py TIP_X/TIP_Y). */
  Glove: 64,
  GloveTipX: 25,
  GloveTipY: 4,
  /** Where on a DOM target the fingertip lands, as a fraction of its box: right of centre, so the glove never hides
   * a card's name. */
  GloveAtX: 0.6,
  GloveAtY: 0.55,
  /** A glove flipped to point down from above aims higher on the target, so its hand covers less of the label. */
  GloveAtYFlipped: 0.3,
  /** A world target's fingertip sits just left of and below the anchor (mockup 3). */
  GloveWorldDx: -4,
  GloveWorldDy: 4,
  /** Keep the glove this far inside the screen before flipping it. */
  GloveEdge: 4,
} as const;

export type Placement = "auto" | "top" | "bottom";

export interface BubbleIn {
  /** The overlay's size. */
  readonly vw: number;
  readonly vh: number;
  /** The bubble's measured height (without the chip). */
  readonly bubbleH: number;
  /** The spotlight, or null for a line with no target. */
  readonly hole: Box | null;
  /** The hole is on the 3D map: the bubble docks to the far band instead of hugging it, so it never covers the map
   * around the target (the other green cells of a soft step stay visible). */
  readonly world: boolean;
  /** The lowest y of the top chrome (HUD, hub top bar), and the highest y of the bottom chrome (tray, controls,
   * tab bar). The bubble docks between them. */
  readonly topBand: number;
  readonly bottomBand: number;
  readonly placement: Placement;
  /** The Skip chip is shown. */
  readonly chip: boolean;
}

export interface BubbleOut {
  /** The bubble's top edge. */
  readonly top: number;
  /** Which way its notch points (at the hole), or none. */
  readonly arrow: "up" | "down" | null;
  /** The notch's x inside the bubble. */
  readonly arrowX: number;
  /** The Skip chip goes on the bubble's far side from the target. */
  readonly chip: "above" | "below";
  /** Docked against a band (top or bottom chrome), or null when hugging the hole. */
  readonly docked: "top" | "bottom" | null;
}

export function overlaps(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

function overlapArea(a: Box, b: Box): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(Math.max(v, lo), Math.max(lo, hi));

/** The notch's x inside the bubble, under `x` on screen (CoachSpot's rule, reused). */
export function arrowXFor(x: number, vw: number): number {
  const L = GuideLayout;
  return Math.round(clamp(x, L.ArrowEdge, vw - L.ArrowEdge) - L.Inset);
}

/** Everything the bubble puts on screen (itself and, when shown, its chip), for the overlap test. */
function footprint(i: BubbleIn, top: number, chip: "above" | "below"): Box[] {
  const L = GuideLayout;
  const boxes: Box[] = [{ x: L.Inset, y: top, w: i.vw - 2 * L.Inset, h: i.bubbleH }];
  if (i.chip) boxes.push({ x: i.vw - L.Inset - L.ChipW, y: chip === "above" ? top - L.ChipSpace : top + i.bubbleH + (L.ChipSpace - L.ChipH), w: L.ChipW, h: L.ChipH });
  return boxes;
}

/**
 * Where the bubble goes (DESIGN §3.2): on the far side of a DOM target, its notch under the target's centre; docked to
 * the far band for a map target or when hugging does not fit; docked to a band when the step asks. It never covers the
 * hole or puts its chip over it; when every option would, the one covering least wins.
 */
export function placeBubble(i: BubbleIn): BubbleOut {
  const L = GuideLayout;
  const chipRoom = i.chip ? L.ChipSpace : 0;
  const dockTopY = i.topBand + L.BandGap;
  const dockBottomY = i.bottomBand - L.BandGap - i.bubbleH;
  const hole = i.hole;
  const arrowX = arrowXFor(hole ? hole.x + hole.w / 2 : i.vw / 2, i.vw);

  const docked = (side: "top" | "bottom"): BubbleOut => {
    // The chip goes on the side away from the chrome it docks against.
    const top = side === "top" ? dockTopY : dockBottomY;
    const chip = side === "top" ? "below" : "above";
    let arrow: BubbleOut["arrow"] = null;
    if (hole && !i.world) {
      if (hole.y >= top + i.bubbleH) arrow = "down";
      else if (hole.y + hole.h <= top) arrow = "up";
    }
    return { top: Math.round(Math.max(0, top)), arrow, arrowX, chip, docked: side };
  };

  if (!hole) return docked(i.placement === "top" ? "top" : "bottom");
  if (i.placement !== "auto") return docked(i.placement);

  const below: BubbleOut = { top: Math.round(hole.y + hole.h + L.Gap), arrow: "up", arrowX, chip: "below", docked: null };
  const above: BubbleOut = { top: Math.round(hole.y - L.Gap - i.bubbleH), arrow: "down", arrowX, chip: "above", docked: null };
  const fitsBelow = below.top + i.bubbleH + chipRoom <= i.bottomBand;
  const fitsAbove = above.top - chipRoom >= i.topBand;
  const cy = hole.y + hole.h / 2;
  const farBand: "top" | "bottom" = cy > (i.topBand + i.bottomBand) / 2 ? "top" : "bottom";
  const nearBand: "top" | "bottom" = farBand === "top" ? "bottom" : "top";

  const options: BubbleOut[] = [];
  if (i.world) {
    options.push(docked(farBand), docked(nearBand));
    if (fitsBelow) options.push(below);
    if (fitsAbove) options.push(above);
  } else {
    const hugFirst = cy < i.vh / 2 ? [below, fitsBelow, above, fitsAbove] as const : [above, fitsAbove, below, fitsBelow] as const;
    if (hugFirst[1]) options.push(hugFirst[0]);
    if (hugFirst[3]) options.push(hugFirst[2]);
    options.push(docked(farBand), docked(nearBand));
  }
  // A docked bubble may take its chip to the other side when the usual side would sit on the hole (over chrome
  // is better than over the thing being taught).
  if (i.chip) for (const o of [...options]) if (o.docked) options.push({ ...o, chip: o.chip === "above" ? "below" : "above" });
  let best = options[0];
  let bestCover = Infinity;
  for (const o of options) {
    const cover = footprint(i, o.top, o.chip).reduce((s, b) => s + overlapArea(b, hole), 0);
    if (cover === 0) return o;
    if (cover < bestCover) { best = o; bestCover = cover; }
  }
  return best;
}

export interface TipIn {
  readonly vw: number;
  readonly vh: number;
  readonly tipH: number;
  /** The thing the tip talks about (ringed), or null. */
  readonly target: Box | null;
  readonly topBand: number;
  readonly bottomBand: number;
  /** Where it sat last layout: kept while still beside a moving target (a raider, a romping pet), so it never jitters. */
  readonly prevTop?: number | null;
}
export interface TipOut { readonly top: number; readonly arrow: "up" | "down" | null; readonly arrowX: number; readonly docked: "top" | "bottom" | null }

/**
 * Where a tip goes (DESIGN §3.4, M45). With a target it sits right beside it, on the roomier side (below a target in
 * the top half, above one in the bottom half), its nib on the target's centre: the tip used to dock above the bottom
 * chrome whatever it talked about, so a tip about the ⚙ menu pointed "up" from 600 px away (Kevin: "the nib doesn't
 * point to where the button is"). It never covers the target; when neither side fits between the chrome bands it
 * docks to the band away from the target. Without a target it docks above the bottom chrome, no nib.
 */
export function placeTip(i: TipIn): TipOut {
  const L = GuideLayout;
  const lo = Math.round(i.topBand + L.BandGap), hi = Math.round(i.bottomBand - L.BandGap - i.tipH);
  const t = i.target;
  const arrowX = arrowXFor(t ? t.x + t.w / 2 : i.vw / 2, i.vw);
  if (!t) return { top: Math.max(0, hi), arrow: null, arrowX, docked: "bottom" };
  const box = (top: number): Box => ({ x: L.Inset, y: top, w: i.vw - 2 * L.Inset, h: i.tipH });
  // beside the target, nudged inside the bands if the target sits in the chrome (a top-bar pill, a tab)
  const below = Math.max(lo, Math.round(t.y + t.h + L.Gap));
  const above = Math.min(hi, Math.round(t.y - L.Gap - i.tipH));
  const fits = (top: number) => top >= lo && top <= hi && !overlaps(box(top), t);
  const upper = t.y + t.h / 2 < (i.topBand + i.bottomBand) / 2;
  const order = upper ? [below, above] : [above, below];
  const arrowFor = (top: number): TipOut["arrow"] => (t.y >= top + i.tipH ? "down" : t.y + t.h <= top ? "up" : null);
  const p = i.prevTop;
  if (p !== undefined && p !== null && fits(p) && arrowFor(p) !== null) {
    const gap = p >= t.y + t.h ? p - (t.y + t.h) : t.y - (p + i.tipH);
    if (gap <= L.Gap + L.TipStick) return { top: Math.max(0, p), arrow: arrowFor(p), arrowX, docked: null };
  }
  for (const top of order) if (fits(top)) return { top: Math.max(0, top), arrow: arrowFor(top), arrowX, docked: null };
  // no room beside it: the band away from the target, still pointing at it; over as little of it as can be
  const far = upper ? hi : lo, near = upper ? lo : hi;
  const top = overlapArea(box(near), t) < overlapArea(box(far), t) ? near : far;
  return { top: Math.max(0, top), arrow: arrowFor(top), arrowX, docked: top === hi ? "bottom" : "top" };
}

export interface GloveOut {
  /** The glove box's top-left. */
  readonly x: number;
  readonly y: number;
  /** Mirrored so it stays on screen: flipX points it from the left, flipY from above. */
  readonly flipX: boolean;
  readonly flipY: boolean;
  /** The fingertip (the ripple's centre). */
  readonly tipX: number;
  readonly tipY: number;
}

/** The glove's fingertip on the target, mirrored when the glove would leave the screen. */
export function placeGlove(target: Box, world: boolean, vw: number, vh: number): GloveOut {
  const L = GuideLayout;
  const tipX = Math.round(world ? target.x + target.w / 2 + L.GloveWorldDx : target.x + target.w * L.GloveAtX);
  const downY = Math.round(world ? target.y + target.h / 2 + L.GloveWorldDy : target.y + target.h * L.GloveAtY);
  const flipX = tipX - L.GloveTipX + L.Glove > vw - L.GloveEdge;
  const flipY = downY - L.GloveTipY + L.Glove > vh - L.GloveEdge;
  const tipY = flipY && !world ? Math.round(target.y + target.h * L.GloveAtYFlipped) : downY;
  const ox = flipX ? L.Glove - L.GloveTipX : L.GloveTipX;
  const oy = flipY ? L.Glove - L.GloveTipY : L.GloveTipY;
  return { x: tipX - ox, y: tipY - oy, flipX, flipY, tipX, tipY };
}

/** A DOM target's spotlight: its rect padded, kept on screen. */
export function domHole(r: Box, pad: number, vw: number, vh: number): Box {
  const x0 = Math.max(0, r.x - pad), y0 = Math.max(0, r.y - pad);
  const x1 = Math.min(vw, r.x + r.w + pad), y1 = Math.min(vh, r.y + r.h + pad);
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
}

/** A world anchor's spotlight: an ellipse `radiusCells` cells across around the anchor. */
export function worldHole(a: { readonly x: number; readonly y: number; readonly r: number }, radiusCells: number): Box {
  const L = GuideLayout;
  const rx = a.r * radiusCells + L.WorldPad;
  const ry = rx * L.WorldAspect;
  return { x: a.x - rx, y: a.y - ry, w: 2 * rx, h: 2 * ry };
}

/** The four tap catchers around a hard block's hole (CoachSpot's geometry): above, below, left, right. */
export function catchers(hole: Box, vw: number, vh: number): [Box, Box, Box, Box] {
  const x0 = Math.max(0, hole.x), y0 = Math.max(0, hole.y);
  const x1 = Math.min(vw, hole.x + hole.w), y1 = Math.min(vh, hole.y + hole.h);
  return [
    { x: 0, y: 0, w: vw, h: y0 },
    { x: 0, y: y1, w: vw, h: Math.max(0, vh - y1) },
    { x: 0, y: y0, w: x0, h: Math.max(0, y1 - y0) },
    { x: x1, y: y0, w: Math.max(0, vw - x1), h: Math.max(0, y1 - y0) },
  ];
}
